<template>
    <section class="FAQSection" :class="{ 'is-light': theme === 'light' }">
        <div class="container">
            <div class="SectionHeader">
                <h2>{{ title }}</h2>
                <p>{{ subtitle }}</p>
            </div>

            <div class="FAQList">
                <div
                    v-for="(faq, index) in faqs"
                    :key="index"
                    class="FAQItem"
                    :class="{ 'is-open': openItems.includes(index) }"
                >
                    <button
                        class="FAQQuestion"
                        @click="toggleFAQ(index)"
                        :aria-expanded="openItems.includes(index)"
                        :aria-controls="`faq-answer-${index}`"
                    >
                        <span class="QuestionText">{{ faq.question }}</span>
                        <i
                            class="ri-arrow-down-s-line FAQIcon"
                            :class="{ 'is-rotated': openItems.includes(index) }"
                        ></i>
                    </button>

                    <div
                        class="FAQAnswer"
                        :id="`faq-answer-${index}`"
                        :aria-hidden="!openItems.includes(index)"
                    >
                        <div class="AnswerContent" v-html="faq.answer"></div>

                        <!-- CTA for specific questions -->
                        <div v-if="faq.showCTA" class="FAQCTA">
                            <router-link
                                :to="
                                    faq.ctaLink ||
                                    (showPromotion
                                        ? '/register?promotion=true&source=faq'
                                        : '/register?source=faq')
                                "
                            >
                                <SiteButton>
                                    {{
                                        faq.ctaText ||
                                        (showPromotion
                                            ? 'Jetzt 50% Rabatt sichern'
                                            : 'Jetzt registrieren')
                                    }}
                                </SiteButton>
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Additional Trust Elements -->
            <div class="FAQFooter">
                <div class="StillHaveQuestions">
                    <h3>Noch Fragen?</h3>
                    <p>Kontaktieren Sie uns für individuelle Beratung</p>
                    <a
                        :href="
                            contactHref ||
                            'mailto:support@taxifusion.de?subject=Frage%20zur%20TaxiFusion'
                        "
                        class="ContactLink"
                    >
                        <SiteButton variant="outline">
                            <i class="ri-mail-line"></i>
                            {{ contactText || 'support@taxifusion.de' }}
                        </SiteButton>
                    </a>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import SiteButton from '@/components/SiteButton.vue';
import { mapGetters } from 'vuex';

export default {
    name: 'SiteFAQ',
    components: {
        SiteButton,
    },
    props: {
        title: {
            type: String,
            default: 'Häufige Fragen',
        },
        subtitle: {
            type: String,
            default: 'Antworten auf Ihre wichtigsten Fragen',
        },
        faqs: {
            type: Array,
            default: () => [],
        },
        contactHref: {
            type: String,
            default: 'mailto:support@taxifusion.de?subject=Frage%20zur%20TaxiFusion',
        },
        contactText: {
            type: String,
            default: 'support@taxifusion.de',
        },
        theme: {
            type: String,
            default: 'dark',
        },
    },
    computed: {
        ...mapGetters('promotion', ['showPromotion']),
    },
    data() {
        return {
            openItems: [0],
        };
    },
    methods: {
        toggleFAQ(index) {
            if (this.openItems.includes(index)) {
                this.openItems = this.openItems.filter((item) => item !== index);
            } else {
                this.openItems.push(index);
            }
        },
    },
    mounted() {
        if (!this.openItems.includes(0)) {
            this.openItems = [0];
        }
    },
};
</script>

<style lang="scss">
.FAQSection {
    padding: 120px 0;
    background: transparent;

    @media (max-width: $breakpoint-tablet-landscape) {
        padding: 80px 0;
    }

    @media (max-width: $breakpoint-mobile) {
        padding: 60px 0;
    }

    .SectionHeader {
        text-align: center;
        margin-bottom: 60px;

        h2 {
            font-family: var(--font-outfit), sans-serif;
            font-size: rem(40px);
            font-weight: 700;
            text-align: center;
            margin-bottom: 2.5rem;
            color: white;
            letter-spacing: -0.02em;

            @media (max-width: $breakpoint-tablet-landscape) {
                font-size: rem(32px);
            }

            @media (max-width: $breakpoint-mobile) {
                font-size: rem(25.6px);
            }
        }

        p {
            font-size: rem(20px);
            color: rgba(255, 255, 255, 0.7);
            text-align: center;
            max-width: 800px;
            margin: 0 auto 4rem;
            line-height: 1.6;

            @media (max-width: $breakpoint-tablet-portrait) {
                text-align: left;
                font-size: rem(17.6px);
                margin-bottom: 3rem;
            }
        }
    }

    .FAQList {
        margin-bottom: 4rem;
    }

    .FAQItem {
        background: rgba(255, 255, 255, 0.02);
        border: 0.5px solid rgba(255, 255, 255, 0.08);
        border-radius: 12px;
        margin-bottom: 1rem;
        overflow: hidden;
        transition: all 0.3s ease;
        backdrop-filter: blur(10px);
        position: relative;

        &:hover {
            border-color: rgba(52, 211, 153, 0.3);
            box-shadow: 0 8px 25px rgba(52, 211, 153, 0.1);
            z-index: 8;
        }

        &.is-open {
            background: rgba(255, 255, 255, 0.05);
            border-color: rgba(52, 211, 153, 0.4);
            z-index: 8;
        }
    }

    .FAQQuestion {
        width: 100%;
        padding: 1.5rem 2rem;
        background: none;
        border: none;
        text-align: left;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        transition: all 0.3s ease;
        border: 0.5px solid rgba(52, 211, 153, 0);
        border-offset: -2px;
        border-radius: 11px;

        &:hover {
            background: rgba(255, 255, 255, 0.05);
        }

        &:focus {
            border: 0.5px solid rgba(52, 211, 153, 0.5);
            z-index: 10;
            position: relative;
        }

        @media (max-width: $breakpoint-mobile) {
            padding: 1.25rem 1.5rem;
        }
    }

    .QuestionText {
        font-size: rem(18px);
        font-weight: 600;
        color: rgba(255, 255, 255, 0.95);
        line-height: 1.4;
        flex: 1;

        @media (max-width: $breakpoint-mobile) {
            font-size: rem(16px);
        }
    }

    .FAQIcon {
        font-size: rem(20px);
        color: rgba(52, 211, 153, 0.8);
        transition: transform 0.3s ease;
        flex-shrink: 0;

        &.is-rotated {
            transform: rotate(180deg);
        }

        @media (max-width: $breakpoint-mobile) {
            font-size: rem(17.6px);
        }
    }

    .FAQAnswer {
        max-height: 0;
        overflow: hidden;
        transition:
            max-height 0.4s ease,
            opacity 0.4s ease;
        opacity: 0;
    }

    .FAQItem.is-open .FAQAnswer {
        max-height: 1000px;
        opacity: 1;
    }

    .AnswerContent {
        padding: 0 2rem 1.5rem;
        color: rgba(255, 255, 255, 0.9);
        line-height: 1.6;
        font-size: rem(16px);

        p {
            margin-bottom: 1rem;
            color: rgba(255, 255, 255, 0.9);
            line-height: 1.6;

            &:last-child {
                margin-bottom: 0;
            }

            &:first-child {
                margin-top: 0;
            }
        }

        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
            color: rgba(255, 255, 255, 0.95);
            font-weight: 600;
            margin-top: 1.5rem;
            margin-bottom: 0.75rem;
            line-height: 1.3;

            &:first-child {
                margin-top: 0;
            }
        }

        h1 {
            font-size: rem(24px);
            font-weight: 700;
            margin-top: 2rem;
            margin-bottom: 1rem;
        }

        h2 {
            font-size: rem(22px);
            font-weight: 600;
            margin-top: 1.75rem;
            margin-bottom: 0.875rem;
        }

        h3 {
            font-size: rem(20px);
            font-weight: 600;
            margin-top: 1.5rem;
            margin-bottom: 0.75rem;
        }

        h4 {
            font-size: rem(18px);
            font-weight: 600;
            margin-top: 1.25rem;
            margin-bottom: 0.625rem;
        }

        h5 {
            font-size: rem(16px);
            font-weight: 600;
            margin-top: 1rem;
            margin-bottom: 0.5rem;
        }

        h6 {
            font-size: rem(14px);
            font-weight: 600;
            margin-top: 0.875rem;
            margin-bottom: 0.5rem;
        }

        a {
            color: #34d399;
            text-decoration: underline;
            text-decoration-color: rgba(52, 211, 153, 0.5);
            text-decoration-thickness: 1px;
            text-underline-offset: 2px;
            transition: all 0.2s ease;
            font-weight: 500;

            &:hover {
                color: #10b981;
                text-decoration-color: rgba(16, 185, 129, 0.8);
                text-decoration-thickness: 2px;
            }

            &:visited {
                color: #059669;
            }

            &:active {
                color: #047857;
            }
        }

        strong,
        b {
            color: rgba(255, 255, 255, 0.98);
            font-weight: 700;
        }

        em,
        i {
            color: rgba(255, 255, 255, 0.85);
            font-style: italic;
        }

        u {
            text-decoration: underline;
            text-decoration-color: rgba(255, 255, 255, 0.6);
            text-underline-offset: 2px;
        }

        mark {
            background: rgba(52, 211, 153, 0.2);
            color: rgba(255, 255, 255, 0.95);
            padding: 0.125rem 0.25rem;
            border-radius: 3px;
        }

        ul,
        ol {
            margin: 1.25rem 0;
            padding-left: 0;

            &:first-child {
                margin-top: 0;
            }

            &:last-child {
                margin-bottom: 0;
            }
        }

        ul {
            list-style: none;

            li {
                margin-bottom: 0.75rem;
                position: relative;
                padding-left: 1.75rem;
                line-height: 1.6;
                color: rgba(255, 255, 255, 0.9);

                &::before {
                    content: '✓';
                    position: absolute;
                    left: 0;
                    top: 0.125rem;
                    color: #34d399;
                    font-size: rem(16px);
                    font-weight: 700;
                    line-height: 1;
                    width: 1.25rem;
                    height: 1.25rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: rgba(52, 211, 153, 0.1);
                    border-radius: 50%;
                    border: 0.5px solid rgba(52, 211, 153, 0.2);
                }

                &:last-child {
                    margin-bottom: 0;
                }

                strong {
                    color: #34d399;
                    font-weight: 600;
                }

                ul,
                ol {
                    margin: 0.5rem 0 0.5rem 1.5rem;

                    li {
                        padding-left: 1.25rem;
                        margin-bottom: 0.5rem;
                        font-size: rem(15.2px);

                        &::before {
                            width: 1rem;
                            height: 1rem;
                            font-size: rem(14px);
                        }
                    }
                }
            }
        }

        ol {
            counter-reset: faq-counter;

            li {
                margin-bottom: 0.75rem;
                position: relative;
                padding-left: 1.75rem;
                line-height: 1.6;
                color: rgba(255, 255, 255, 0.9);
                counter-increment: faq-counter;

                &::before {
                    content: counter(faq-counter) '.';
                    position: absolute;
                    left: 0;
                    top: 0.125rem;
                    color: #34d399;
                    font-size: rem(14.4px);
                    font-weight: 600;
                    width: 1.25rem;
                    text-align: center;
                    background: rgba(52, 211, 153, 0.05);
                    border-radius: 50%;
                    border: 0.5px solid rgba(52, 211, 153, 0.15);
                    line-height: 1.2;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    height: 1.25rem;
                }

                &:last-child {
                    margin-bottom: 0;
                }

                strong {
                    color: #34d399;
                    font-weight: 600;
                }

                ul,
                ol {
                    margin: 0.5rem 0 0.5rem 1.5rem;

                    li {
                        padding-left: 1.25rem;
                        margin-bottom: 0.5rem;
                        font-size: rem(15.2px);

                        &::before {
                            width: 1rem;
                            height: 1rem;
                            font-size: rem(14px);
                        }
                    }
                }
            }
        }

        blockquote {
            margin: 1.5rem 0;
            padding: 1rem 1.5rem;
            background: rgba(255, 255, 255, 0.03);
            border-left: 4px solid #34d399;
            border-radius: 0 8px 8px 0;
            color: rgba(255, 255, 255, 0.85);
            font-style: italic;
            position: relative;

            &:first-child {
                margin-top: 0;
            }

            &:last-child {
                margin-bottom: 0;
            }

            &::before {
                content: '"';
                position: absolute;
                left: 0.5rem;
                top: 0.5rem;
                color: rgba(52, 211, 153, 0.3);
                font-size: rem(32px);
                font-family: serif;
            }
        }

        code {
            background: rgba(255, 255, 255, 0.1);
            color: rgba(255, 255, 255, 0.95);
            padding: 0.125rem 0.375rem;
            border-radius: 4px;
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            font-size: rem(14px);
            font-weight: 500;
            border: 0.5px solid rgba(255, 255, 255, 0.1);
        }

        pre {
            background: rgba(255, 255, 255, 0.05);
            border: 0.5px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            padding: 1rem;
            overflow-x: auto;
            margin: 1.25rem 0;
            font-size: rem(14.4px);
            line-height: 1.5;

            code {
                background: transparent;
                padding: 0;
                border-radius: 0;
                border: none;
                font-size: inherit;
            }

            &:first-child {
                margin-top: 0;
            }

            &:last-child {
                margin-bottom: 0;
            }
        }

        hr {
            border: none;
            height: 1px;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
            margin: 1.5rem 0;
        }

        table {
            width: 100%;
            border-collapse: collapse;
            margin: 1rem 0;
            background: rgba(255, 255, 255, 0.02);
            border-radius: 8px;
            overflow: hidden;
        }

        th,
        td {
            padding: 0.75rem;
            text-align: left;
            border-bottom: 0.5px solid rgba(255, 255, 255, 0.1);
            color: rgba(255, 255, 255, 0.9);
        }

        th {
            background: rgba(255, 255, 255, 0.05);
            font-weight: 600;
            color: rgba(255, 255, 255, 0.95);
        }

        tr:last-child td {
            border-bottom: none;
        }

        span {
            color: inherit;
        }

        div {
            color: inherit;
            line-height: inherit;
        }

        br {
            content: '';
            display: block;
            margin: 0.5rem 0;
        }

        @media (max-width: $breakpoint-mobile) {
            padding: 0 1.5rem 1.25rem;

            h1 {
                font-size: rem(22px);
            }
            h2 {
                font-size: rem(20px);
            }
            h3 {
                font-size: rem(18px);
            }
            h4 {
                font-size: rem(16px);
            }
            h5 {
                font-size: rem(14.4px);
            }
            h6 {
                font-size: rem(12.8px);
            }

            ul,
            ol {
                margin: 1rem 0;

                li {
                    padding-left: 1.5rem;
                    margin-bottom: 0.625rem;
                    font-size: rem(14.4px);

                    &::before {
                        width: 1rem;
                        height: 1rem;
                        font-size: rem(14px);
                        left: 0;
                    }
                }
            }

            blockquote {
                padding: 0.75rem 1rem;
                margin: 1rem 0;
            }

            pre {
                padding: 0.75rem;
                font-size: rem(13.6px);
            }
        }
    }

    .FAQCTA {
        margin-top: 1.5rem;
        margin-bottom: 1rem;
        padding-top: 1rem;
        padding-bottom: 0.5rem;
        border-top: 0.5px solid rgba(255, 255, 255, 0.1);
        text-align: center;
    }

    .FAQFooter {
        @media (max-width: $breakpoint-tablet-landscape) {
            grid-template-columns: 1fr;
            gap: 2rem;
        }
    }

    .StillHaveQuestions {
        background: var(--color-blue-dark);
        border: 0.5px solid rgba(255, 255, 255, 0.08);
        border-radius: 12px;
        padding: 2rem;
        backdrop-filter: blur(10px);
        text-align: center;
        position: relative;
        z-index: 6;

        h3 {
            font-size: rem(20px);
            color: rgba(255, 255, 255, 0.95);
            margin-bottom: 0.5rem;
            font-weight: 600;
        }

        p {
            color: rgba(255, 255, 255, 0.8);
            margin-bottom: 1.5rem;
            font-size: rem(14.4px);
        }

        .ContactLink {
            text-decoration: none;
        }

        @media (max-width: $breakpoint-mobile) {
            padding: 1.5rem;

            h3 {
                font-size: rem(18px);
            }
        }
    }

    .container {
        max-width: 800px;
        margin: 0 auto;
        padding: 0 2rem;

        @media (max-width: $breakpoint-tablet-landscape) {
            padding: 0 1.5rem;
        }

        @media (max-width: $breakpoint-mobile) {
            padding: 0 1rem;
        }
    }

    &.is-light {
        background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);

        .SectionHeader {
            h2 {
                color: #1a202c;
            }

            p {
                color: #64748b;
            }
        }

        .FAQItem {
            background: white;
            border: 0.5px solid #e2e8f0;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);

            &:hover {
                border-color: #188d66;
                box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
            }

            &.is-open {
                background: white;
                border-color: #188d66;
                box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
            }
        }

        .FAQQuestion {
            &:hover {
                background: #f8fafc;
            }

            &:focus {
                border-color: #188d66;
            }
        }

        .QuestionText {
            color: #1f2937;
        }

        .FAQIcon {
            color: #188d66;
        }

        .FAQAnswer {
            border-top: 0.5px solid #e2e8f0;
            background-color: #f8fafc;
        }

        .AnswerContent {
            color: #374151;
            margin-top: 12px;
            p {
                color: #111827;
                font-size: rem(16px);
            }

            h1,
            h2,
            h3,
            h4,
            h5,
            h6 {
                color: #1f2937;
            }

            a {
                color: #188d66;

                &:hover {
                    color: #047857;
                }
            }

            strong,
            b {
                color: #111827;
            }

            ul li {
                color: #374151;

                &::before {
                    color: #188d66;
                    background: rgba(24, 141, 102, 0.1);
                    border-color: rgba(24, 141, 102, 0.2);
                }

                strong {
                    color: #188d66;
                }
            }

            ol li {
                color: #374151;

                &::before {
                    color: #188d66;
                    background: rgba(24, 141, 102, 0.05);
                    border-color: rgba(24, 141, 102, 0.15);
                }

                strong {
                    color: #188d66;
                }
            }

            blockquote {
                background: #f8fafc;
                border-left-color: #188d66;
                color: #4b5563;
            }

            code {
                background: #e5e7eb;
                color: #111827;
                border-color: #d1d5db;
            }

            pre {
                background: #f3f4f6;
                border-color: #e5e7eb;

                code {
                    background: transparent;
                    border: none;
                }
            }

            hr {
                background: linear-gradient(90deg, transparent, #e5e7eb, transparent);
            }

            table {
                background: white;
            }

            th,
            td {
                border-bottom-color: #e5e7eb;
                color: #374151;
            }

            th {
                background: #f9fafb;
                color: #1f2937;
            }
        }

        .FAQCTA {
            border-top-color: #e5e7eb;
        }

        .StillHaveQuestions {
            background: var(--color-blue-dark);
            border: 0.5px solid rgba(255, 255, 255, 0.08);
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);

            h3 {
                color: rgba(255, 255, 255, 0.95);
            }

            p {
                color: rgba(255, 255, 255, 0.8);
            }
        }
    }
}
</style>
